import React, { useEffect, useRef, useState } from "react";
import { BooleanParam, StringParam, useQueryParam } from "use-query-params";
import { notification } from "antd";
import styled from "styled-components";

import useEmailParam from "../../hooks/useEmailParam";
import { EmailDocument } from "../../../api/graphql";
import EmailDocumentPreview from "./EmailDocumentPreview";
import Header from "./Header";
import { useEmailDocumentQuery } from "../../../api/graphql/queries/emailDocuments";
import TransactionListContainer from "./TransactionList/TransactionListContainer";
import EmptyWrapper from "../../common/EmptyWrapper";
import NoMatchSection from "./NoMatchSection";
import { ContentWrapper } from "../../common/styledComponents";
import { KontaxUserContext } from "../../contexts/KontaxUserContext";
import { DocumentFilterOptions, KontaxUserStatus } from "@/v1/types";
import FilterBoxCard from "../../common/Card/FilterBoxCard";
import RadioButtonGroup from "../../common/RadioButtonGroup";

const PreviewAndListWrapper = styled.div`
  display: flex;
  flex-direction: row;
  gap: 12px;
`;

const RightSection = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 24px;
`;

const FilterWrapper = styled.div`
  display: flex;
  flex: 1;
  flex-direction: row;
  gap: 40px;
  padding-bottom: 40px;
`;

const HeaderWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const BelegeView = () => {
  const [email, setEmailParam] = useEmailParam() as [string, Function];
  const [random, setRandom] = useQueryParam("random", BooleanParam) as [
    boolean,
    Function
  ];
  const [id, setId] = useQueryParam("id", StringParam) as [string, Function];
  const [emailDocument, setEmailDocument] = useState<EmailDocument | null>(
    null
  );
  const [totalUnmatched, setTotalUnmatched] = useState<number>(0);

  const [offset, setOffset] = useState<number>(0);

  const [filterOptions, setFilterOptions] = useState<DocumentFilterOptions>({
    kontaxStatus: KontaxUserStatus.ACTIVATED,
    manualMatchRequired: false,
  });
  const hasRetriedRef = useRef(false);

  const [getEmailDocument] = useEmailDocumentQuery({
    onCompleted: (data: { emailDocument: EmailDocument | null }) => {
      const document = data.emailDocument;
      const unmatched = document?.totalUnmatched ?? 0;

      setEmailDocument(document);
      setTotalUnmatched(unmatched);

      // If we got a document, reset the retry flag
      if (document) {
        hasRetriedRef.current = false;
      }

      // If no document returned but totalUnmatched > 0, reset offset and retry (only once)
      if (
        !document &&
        totalUnmatched > 0 &&
        offset !== 0 &&
        !hasRetriedRef.current
      ) {
        hasRetriedRef.current = true;
        setOffset(0);

        getEmailDocument({
          variables: {
            email: !!random ? "" : email,
            options: {
              offset: 0,
              ...filterOptions,
            },
          },
        });
        return;
      }

      if (document?.id && document.id !== id) {
        setId(document.id);
      }

      if (!!random && document?.userEmail) {
        setEmailParam(document.userEmail);
      }

      if (email && !random && document) {
        setOffset((prev) => prev + 1);
      }
    },
    onError: (err: Error) => {
      notification.error({ message: `An error occurred: ${err.message}` });
    },
  });

  const fetchNext = () => {
    const nextOffset = random ? 0 : offset;
    getEmailDocument({
      variables: {
        email: !!random ? "" : email,
        options: {
          offset: nextOffset,
          ...filterOptions,
        },
      },
    });
  };

  useEffect(() => {
    if (random || !email) {
      setOffset(0);
    }
  }, [random, email]);

  useEffect(() => {
    if (email && !random && filterOptions.kontaxStatus !== undefined) {
      setFilterOptions({ ...filterOptions, kontaxStatus: undefined });
    }
  }, [filterOptions, random, email]);

  useEffect(() => {
    if (!email) {
      setId(null);
      setRandom(true);
      setEmailDocument(null);
      setOffset(0);
      getEmailDocument({
        variables: {
          options: {
            ...filterOptions,
            offset: 0,
          },
        },
      });
    } else {
      getEmailDocument({
        variables: {
          email,
          options: {
            id,
            offset,
            ...filterOptions,
          },
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email, setId, getEmailDocument]);

  useEffect(() => {
    if (!!random) {
      getEmailDocument({
        variables: {
          options: {
            ...filterOptions,
            offset: 0,
          },
        },
      });
    } else if (
      email &&
      !random &&
      filterOptions.manualMatchRequired !== undefined
    ) {
      getEmailDocument({
        variables: {
          email,
          options: {
            offset,
            ...filterOptions,
          },
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [random, filterOptions, getEmailDocument]);

  return (
    <ContentWrapper>
      <KontaxUserContext email={email}>
        <FilterWrapper>
          <HeaderWrapper>
            <Header
              setEmail={(email: string) => {
                setRandom(false);
                setId(null);
                setOffset(0);
                setEmailParam(email);
              }}
              email={email}
            />
            {!random && `${totalUnmatched || 0} receipts found`}
          </HeaderWrapper>
          <FilterBoxCard
            data-test="transactionDirectionTypeRadios"
            title="Type"
          >
            <RadioButtonGroup
              value={filterOptions.kontaxStatus}
              options={[
                { label: "All", value: undefined },
                {
                  label: KontaxUserStatus.ACTIVATED,
                  value: KontaxUserStatus.ACTIVATED,
                },
                {
                  label: KontaxUserStatus.DEACTIVATED,
                  value: KontaxUserStatus.DEACTIVATED,
                },
              ]}
              onChange={(value: KontaxUserStatus) => {
                setFilterOptions({ ...filterOptions, kontaxStatus: value });
                setOffset(0);
              }}
            />
          </FilterBoxCard>
          <FilterBoxCard data-test="statusRadios" title="Status">
            <RadioButtonGroup
              value={filterOptions.manualMatchRequired}
              options={[
                { label: "Normal", value: false },
                { label: "Send to Customer", value: true },
              ]}
              onChange={(value: boolean) => {
                setFilterOptions({
                  ...filterOptions,
                  manualMatchRequired: value,
                });
                setOffset(0);
              }}
            />
          </FilterBoxCard>
        </FilterWrapper>
        {!emailDocument && (
          <EmptyWrapper
            description="No more documents! 👏"
            buttonProperties={{
              text: "Refresh",
              onClick: fetchNext,
            }}
          />
        )}
        {emailDocument && (
          <PreviewAndListWrapper>
            <EmailDocumentPreview
              emailDocument={emailDocument}
              fetchNext={fetchNext}
            />
            <RightSection>
              <TransactionListContainer
                transactions={emailDocument.transactionMatches}
                email={email}
                currentEmailDocument={emailDocument}
                onMatchSuccess={fetchNext}
              />
              <NoMatchSection
                email={email}
                emailDocument={emailDocument}
                onCreateExternalTransaction={fetchNext}
              />
            </RightSection>
          </PreviewAndListWrapper>
        )}
      </KontaxUserContext>
    </ContentWrapper>
  );
};
export default BelegeView;
